import { LitElement, html, css, nothing } from "lit";
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { property, customElement } from "lit/decorators.js";
import { SvgIcons } from "../voyage-svg-icons/src/voyage-svg-icons";
import "@maersk-global/mds-components-core/mc-modal";
import { VoyFleetAlarmModel } from "../../src/models/voy-fleet-alarm-model";
import {
  AlarmEvents,
  FleetAlarm,
} from "../../src/models/types/voy-fleet-alarm-model.types";
import {
  filterAlarms,
  filterNotifications,
  filterWeatherRoutingAuditLogs,
  formatTimeDifference,
  getAuditLogMessage,
  getBPRNotificationMessage,
  getNotificationMessage,
  getOfflineNotificationMessage,
  getTooltipContent,
  getWeatherAlarmIcon,
} from "./helper";
import { AlarmState, AlarmType } from "../../src/services/types/fleet-alarms-service.types";
import { AlertId, VesselAlertsModel } from "../../src/models/vessel-alerts-model";
import { AuthorizationRoles, AuthService } from "../../src/services/auth-service";
import { FiSubView, UiStateModel, UiStateModelParam } from "../../src/models/ui-state-model";
import { FI_EVENT_CATEGORIES, FI_VESSEL_LIST_ACTIONS, TraceService } from "../../src/services/trace-service";
import { NotificationEvents, VoyFleetNotificationModel } from "../../src/models/voy-fleet-notification-model";
import { NotificationType } from "../../src/services/types/fleet-notification-service.types";
import '@maersk-global/mds-components-core/mc-tag';
import { AuditLog, AuditLogState, AuditLogStatus, WeatherRoutingNotificationEvents } from "../../src/services/types/fleet-weather-routing-notification.types";
import { FleetNotificationService } from "../../src/services/fleet-notification-service";

const enum Tab {
  HeavyWeather = "HeavyWeather",
  Information = "Information",
  WeatherRouting = "WeatherRouting",
}

@customElement("voy-activity-log")
export class VoyActivityLog extends LitElement {
  @property({ type: Array, reflect: true }) activityLog: FleetAlarm[] = []
  @property({ type: String }) searchQuery: string = ''
  @property({ type: Boolean }) needAction: boolean = false
  @property({ type: String }) activeTab: string = Tab.HeavyWeather;
  @property({ type: Array }) bprNotification: NotificationType[] = [];
  @property({ type: Array }) loadBprRejectList: NotificationType[] = [];
  @property({ attribute: false }) private _startVal: number = 200;
  @property({type: Boolean}) hasAlarmAccess: boolean = AuthService.userHasRole(AuthorizationRoles.AlarmEdit); // VET access 
  @property({ type: Array }) weatherRoutingAuditLogs: AuditLog[] = [];
  @property ({type: Array})   filteredWeatherRoutingAuditLogs : AuditLog[] = [];
  @property({ type: Boolean }) rejectedLogShown: boolean = false;
  @property({ attribute: false }) private offlineNotifications?:
    | NotificationType[]
    | null = [];

  static get styles() {
    return [
      css`
        :host {
          display: block;
          font-family: Arial, sans-serif;
        }
        .activity-log {
          width: 400px;
          border-radius: 4px;
          background-color: #fff;
        }
        .activity-item {
          border-bottom: 2px solid #eee;
          padding: 15px;
        }
        .activity-item:last-child {
          border-bottom: none;
          margin-bottom: 20px; 
        }
        .activity-type {
          cursor: pointer;
          display: flex;
          align-items: center;
          margin-bottom: 10px;
        }
        .activity-icon {
          width: 24px;
          height: 24px;
          margin-right: 12px;
          margin-bottom: 10px;
        }
        .activity-message {
          flex: 1;
          font-size: 14px;
          line-height: 1.5;
          font-family: 'MaerskText';
        }
        .activity-time {
          font-size: 12px;
          color: #999;
          white-space: nowrap;
          text-align: right;
          margin-left: auto;
        }
        .activity-actions {
          display: flex;
          gap: 13px;
          font-size: 14px;
          /* color: #007bff; */
          margin-top: 12px;
          margin-left: 36px;
          font-family: 'MaerskText';
          justify-content: space-between;
        }
        .activity-actions .add-note,
        .activity-actions .view-note {
          cursor: pointer;
        }
        .activity-actions .add-note:hover,
        .activity-actions .view-note:hover {
          text-decoration: underline;
        }
        .accept-button {
          margin-top: 10px;
          padding-left: 36px;
          font-family: 'MaerskText';
        }
        .activity-item.active {
          background-color: #42B0D51C;
          border-left: 4px solid #42B0D51C;
        }
        .activity-message.active, .alarm-details.active {
          font-weight: bold;
        }
        .separator {
          color: #999;
          align-self: center;
        }
        .add-note,
        .view-note {
          gap: 5px;
          align-items: center;
          display: flex;
        }
        .view-note.disabled {
        pointer-events: none;
        opacity: 0.5;
        cursor: not-allowed;
      }
        .search-container {
          padding: 10px;
          border-bottom: 1px solid #eee;
        }
        .search-container input {
          width: 100%;
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
          height: 37px;
          box-sizing: border-box;
          border-radius: 5px;
        }
        .no-alarms {
          padding: 20px;
          display: flex;
          justify-content: center;
        }
        .search-input-wrapper {
          position: relative;
          display: inline-block;
          width: 100%;
        }
        .search-icon {
          position: absolute;
          left: 10px; 
          top: 55%;
          transform: translateY(-50%);
          pointer-events: none;
        }
        .search-container input[type="text"] {
          width: 100%;
          padding-left: 35px;
          box-sizing: border-box;
        }
        .alarm-details {
          margin-left: 36px;
          font-size: 14px;

          svg {
            margin-bottom: -2px;
          }
        }
        .tabs {
          display: flex;
          position: relative;
          border-bottom: 2px solid #dbdbdb;
          margin-bottom: 20px;
          margin-top: 10px;
          width: 100%
        }

        .tab {
          cursor: pointer;
          color: #545454;
          font-weight: 400;
          font-family: 'MaerskText';
          margin-bottom: -2px;
          padding-bottom: 10px;
          transition: background-color 0.3s, color 0.3s;
          display: flex;
          position: relative;
          width: 50%
        }

        .tab:hover {
          background-color: #f0f0f0;
        }

        .tab.active {
          color: #141414;
          border-bottom: 2px solid #00243d;
        }

        .each-tab-text {
          margin-top: 15px;
          margin-bottom: 10px;
          display: flex;
          justify-content: center;
          width: 100%
        }
        .each-tab-count {
          background: var(--Color-Background-Info-weak, #d3ecf9);
          color: black;
          border-radius: 50%;
          font-family: 'MaerskText';
          font-size: 12px;
          font-weight: 400;
          line-height: 16px;
          text-align: center;
          opacity: 1;
          padding-left: 6px;
          padding-right: 6px;
          padding-top: 0px;
          padding-bottom: 1px;
          position: absolute;
          right: 0px;
        }
        .dismissed-count{
          display: flex;
          align-items: center;
          justify-content: end;
        }
        .wr-activity-actions{
          margin-top: 0;
          margin-left: 16px;
        }
        .option-icon {
          cursor: pointer;
          margin-top: 4px
        }
      `,
    ];
  }
  constructor() {
    super();

    this._setWeatherAlarms = this._setWeatherAlarms.bind(this);
    this._onAcceptAlarm = this._onAcceptAlarm.bind(this);
    this._setBprRejectNotifications = this._setBprRejectNotifications.bind(this);
    this._handleScroll = this._handleScroll.bind(this);
    this._setWeatherRoutingNotifications = this._setWeatherRoutingNotifications.bind(this);
    this.renderWeatherRoutingAuditLog = this.renderWeatherRoutingAuditLog.bind(this);
    this.getDismissedCountByImo = this.getDismissedCountByImo.bind(this);
    this.weatherRoutingNotificationclick = this.weatherRoutingNotificationclick.bind(this);
    this._checkForOfflineVesselNotification = this._checkForOfflineVesselNotification.bind(this);
    
  }

  connectedCallback(): void {
    super.connectedCallback();

    VoyFleetAlarmModel?.addEventListener(
      AlarmEvents.NotificationSenderAlarmsUpdated,
      this._setWeatherAlarms
    );
    
    this._setBprRejectNotifications();
    this.addEventListener("scroll", this.debounce(this._handleScroll,500));
    this._setWeatherRoutingNotifications(); 
    VoyFleetNotificationModel?.addEventListener(WeatherRoutingNotificationEvents.FETCHALL, this._setWeatherRoutingNotifications);
    VoyFleetNotificationModel!.addEventListener(NotificationEvents.OfflineNotificationsUpdated, this._checkForOfflineVesselNotification);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    VoyFleetAlarmModel?.removeEventListener(
      AlarmEvents.NotificationSenderAlarmsUpdated,
      this._setWeatherAlarms
    );
    this.removeEventListener("scroll", this.debounce(this._handleScroll,500));

    VoyFleetNotificationModel?.removeEventListener(WeatherRoutingNotificationEvents.FETCHALL, this._setWeatherRoutingNotifications);
    VoyFleetNotificationModel!.removeEventListener(NotificationEvents.OfflineNotificationsUpdated, this._checkForOfflineVesselNotification);

  }


  private async _checkForOfflineVesselNotification() {
    if(this.hasAlarmAccess) this.offlineNotifications = VoyFleetNotificationModel?.offlineVesselNotifications;
    this.requestUpdate();
  }

  private _handleScroll():void {

    const remainingItems = this.bprNotification.length - this.loadBprRejectList.length;
    if (remainingItems > 0) {
        // Calculate the number of items to copy in the current iteration (up to 200)
        const itemsToCopy = Math.min(remainingItems, 200);
        this.loadBprRejectList = [
            ...this.loadBprRejectList,
            ...this.bprNotification.slice(this._startVal, (this._startVal + itemsToCopy))
        ];

        this._startVal = this._startVal + itemsToCopy;
    } 
}

  private _setWeatherAlarms() {
    const alarms = VoyFleetAlarmModel?.getWeatherAlarmsForNotificationSender ?? [];
      this.activityLog = alarms;
      this.requestUpdate();
  }

  private async _setBprRejectNotifications(){
    const bprRejectLog = await VoyFleetNotificationModel?.getBprRejectLog("BprReject");
    this.bprNotification = bprRejectLog.filter((notification: { state: string }) => notification.state !== 'Active')
    this.loadBprRejectList = this.bprNotification.slice(0,200);
  }

  deeplinkToVesselOverView(event: Event, imo: number){
        event.stopPropagation();
        UiStateModel!.setValue(UiStateModelParam.IMO, imo.toString());
        UiStateModel!.setValue(UiStateModelParam.PORT);
        UiStateModel!.setValue(UiStateModelParam.PortRegion);
        UiStateModel!.setValue(UiStateModelParam.FISubView, FiSubView.VesselDetails);
        UiStateModel!.setLeftPanelVisibility(true,"hide-anchor");
        TraceService!.event(
            FI_EVENT_CATEGORIES.VESSEL_LIST,
            FI_VESSEL_LIST_ACTIONS.SELECTED_VESSEL,
            imo.toString()
        );
  }

  private weatherRoutingNotificationclick(event: Event, auditLog: AuditLog) {
    event.stopPropagation();
    const userInfo = AuthService.getCurrentUserId()   

    const logForstatusChange = {
      status: 'Read',
      updated_by: userInfo,
      id: auditLog.id
    }
      try {
        VoyFleetNotificationModel?.updateWeatherRoutingNotifications(logForstatusChange);
        this.requestUpdate(); 
      } catch (error) {
        console.error('Error updating weather log state:', error);
      }
    this.deeplinkToVesselOverView(event, auditLog.imo);
    this._setWeatherRoutingNotifications(); //update log info with latest status
  }

  private async handleNotificationClick(event: Event, alarm: NotificationType) {
    event.stopPropagation();

    const stateToUpdate = alarm.type === "vesseloffline" ? AlarmState.InProgress : AlarmState.Acknowledged;
    try {
      const payload = {
        data: JSON.stringify("Acknowledged by VET"),
        state: stateToUpdate
      }
      VoyFleetNotificationModel?.updateNotification(alarm.id,payload, alarm.type);
      alarm.state === stateToUpdate;
      if (alarm.type === "vesseloffline") {
        VoyFleetNotificationModel?.dispatchEvent(
          new Event(NotificationEvents.OfflineNotificationsUpdated)
        );
      }
    } catch (error) {
      console.error('Error updating alarm state:', error);
    }

    // Navigate to Vessel Overview
    this.deeplinkToVesselOverView(event, alarm.imo_no);
  }

  handleAddNote(event: Event, selectedAlarm: FleetAlarm | AuditLog, isAuditLog?: boolean) {
    event.stopPropagation();
    const addNoteEvent = new CustomEvent("show-add-note-popup", {
      bubbles: true,
      composed: true,
      detail: { selectedAlarm , isAuditLog},
    });
    this.dispatchEvent(addNoteEvent);
  }

  handleViewNote(event: Event, selectedAlarm: FleetAlarm | AuditLog, isAuditLog?: boolean) {
    event.stopPropagation();
    const viewNoteEvent = new CustomEvent("show-view-note-popup", {
      bubbles: true,
      composed: true,
      detail: { selectedAlarm, isAuditLog },
    });
    this.dispatchEvent(viewNoteEvent);
  }

  handleSearchInput(event: Event) {
    this.searchQuery = (event.target as HTMLInputElement).value.toLowerCase();
  }

  private handleTabClick(tab: string) {
    this.activeTab = tab
    this.rejectedLogShown = false;
    this.requestUpdate() // Trigger re-render
  }

  private async _setWeatherRoutingNotifications(){
    const result = await VoyFleetNotificationModel?.getWeatherRoutingNotifications();
    if(result){
      this.weatherRoutingAuditLogs = result;
    }
  }

  render() {
    // Filter the activity log based on the search query
    let FilteredAlarms: any = []
    let FilteredAlarmsHeavyWeather: FleetAlarm[] = []
      FilteredAlarmsHeavyWeather = filterAlarms(
        this.activityLog,
        this.searchQuery,
        this.needAction
      )
    const activeAlarmsCountHeavyWeather = FilteredAlarmsHeavyWeather.filter(
      (alarm: { alarmState: string }) => alarm.alarmState === 'active'
    ).length
    const weatherRoutingAuditLogsCount = this.weatherRoutingAuditLogs.filter( log => log.status.toLowerCase() === AuditLogState.UNREAD).length;
    const informationNotifications = [...this.bprNotification, ...(this.offlineNotifications ?? [])];
    const activeAlarmsCountInformation = informationNotifications?.filter((notification) => notification.state.toLocaleLowerCase() === AlarmState.Active).length;
    if (this.activeTab === Tab.HeavyWeather) {
      FilteredAlarms = FilteredAlarmsHeavyWeather
    }

    let FilteredInformations: NotificationType[] = []
    FilteredInformations = filterNotifications(
      informationNotifications,
      this.searchQuery,
      this.needAction
    );

    if (this.activeTab === Tab.Information) {
      FilteredAlarms = FilteredInformations
    }


    this.filteredWeatherRoutingAuditLogs = filterWeatherRoutingAuditLogs(
      this.weatherRoutingAuditLogs,
      this.searchQuery,
      this.needAction
    );

    if (this.activeTab === Tab.WeatherRouting) {
      FilteredAlarms = this.filteredWeatherRoutingAuditLogs
    }
    const tabs = [Tab.WeatherRouting, Tab.HeavyWeather, Tab.Information]

    // const tabs = [Tab.HeavyWeather, Tab.Information] not showing information tab now

    return html`
      <div class="activity-log">
        <div class="search-container">
          <div class="search-input-wrapper">
            <span class="search-icon">${SvgIcons.customSearch}</span>
            <input
              type="text"
              placeholder="Search vessel, IMO"
              @input=${this.handleSearchInput}
            />
          </div>
        </div>
        <div class="tabs">
          ${tabs.map(
            (tab) => html`
              <div
                class="tab ${this.activeTab === tab ? 'active' : ''}"
                @click=${() => this.handleTabClick(tab)}
              >
                <span class="each-tab-text"
                  >${tab.replace(/([A-Z])/g, ' $1').trim()}
                </span>
                ${tab === Tab.HeavyWeather && activeAlarmsCountHeavyWeather > 0
                  ? html`<span class="each-tab-count"
                      >${activeAlarmsCountHeavyWeather}</span
                    >`
                  : ''}
                ${tab === 'WeatherRouting' && weatherRoutingAuditLogsCount > 0

                  ? html`<span class="each-tab-count"
                      >${weatherRoutingAuditLogsCount}</span
                    >`
                  : ''}
                ${tab === Tab.Information && activeAlarmsCountInformation > 0
                  ? html`<span class="each-tab-count"
                      >${activeAlarmsCountInformation}</span
                    >`
                  : ''}
              </div>
            `
          )}
        </div>

         ${FilteredAlarms.length == 0 || !FilteredAlarms
          ? html`<div class="no-alarms">No alarms found</div>`
          : this.activeTab === Tab.HeavyWeather ? FilteredAlarms.map((item: FleetAlarm ) =>
              this.renderNotification(item)
            ) :  this.activeTab === Tab.Information ? FilteredAlarms.map((item: NotificationType ) =>
              this.renderInformationNotification(item)
            ) : FilteredAlarms.map((log: AuditLog ) => this.renderWeatherRoutingAuditLog(log))}

      </div>
    `
  }

  private async   _onAcceptAlarm(alarm: FleetAlarm) {
    const response = await VoyFleetAlarmModel?.updateWeatherAlarm(alarm.id, AlarmState.InProgress, "Accepted", "");
    if(response){
      const alertType =
      alarm?.alarmType === AlarmType.HeavyWeather
        ? AlertId.HeavyWeather
        : AlertId.ParametricRoll;
        VesselAlertsModel!.addAlert(alarm.imo, {
          id: `${alertType}-${alarm.imo}`,
          message: "Alarm accepted"
      });
      }
    VoyFleetAlarmModel!._fetchAlarmsForNotificationSender();

  }

  private renderNotification(alarm: FleetAlarm) {
    if (!alarm) return "";
    const isActiveAlarm = alarm.alarmState === AlarmState.Active;
    const isDisabledViewNote = alarm?.alarmLogs?.filter(log => log.note !== "" && log.userId !== "system")?.length === 0;

    return html`
      <div
        class="activity-item ${isActiveAlarm
          ? "active"
          : ""}"
         @click=${(e:Event) => this.deeplinkToVesselOverView(e, alarm.imo)}
       >
        <div class="activity-type">
          <div class="activity-icon">
            ${getWeatherAlarmIcon(alarm.alarmType)}
          </div>
          <div
            class="activity-message ${isActiveAlarm
              ? "active"
              : ""}"
          >
            ${getNotificationMessage(alarm)}
          </div>
        </div>
        <span class="alarm-details ${isActiveAlarm
          ? "active"
          : ""}">
            Alarm Details 
            <span>${SvgIcons.infoCircle}
              <voyage-tooltip-proxy
                  text="${getTooltipContent(alarm)}"
                  html-content
                  .positionPriority=${["top"]}>
              </voyage-tooltip-proxy>
            </span>
        </span>
        <div class="activity-actions">
          <div class="add-note" @click=${(e: Event) => this.handleAddNote(e, alarm)}>
            <div>${SvgIcons.activityLogIcon}</div>
            <div>Add note</div>
          </div>
          <div class="separator">|</div>
          <div class="view-note ${isDisabledViewNote ? 'disabled' : ''}"  @click=${(e: Event) => isDisabledViewNote ? nothing :  this.handleViewNote(e, alarm)}>
            <div>${SvgIcons.activityLogIcon}</div>
            <div>View note</div>
          </div>
          <div class="activity-time">
            ${formatTimeDifference(alarm.alarmLogs?.[0].createdAt ?? "")}
          </div>
        </div>
        <div class="accept-button">
          ${isActiveAlarm && this.hasAlarmAccess
            ? html`<mc-button
                focusstartanchor
                appearance="primary"
                variant="filled"
                  fit="small"
                @click=${() => {
                  this._onAcceptAlarm(alarm);
                }}
                >Accept</mc-button
              >`
            : ""}
        </div>
      </div>
    `;
  }

  private renderInformationNotification(alarm: NotificationType) {
    if (!alarm) return "";
    
    const isActiveAlarm = alarm.state.toLocaleLowerCase() === AlarmState.Active;

    const message: {body: string, icon: SvgIcons} = {
      body: "",
      icon: ""
    }

    // expand as more notification types are added when designs ar ready
    switch (alarm.type) {
      case "vesseloffline":
        message.body = getOfflineNotificationMessage(alarm);
        message.icon = SvgIcons.cloudofflineCircle;
        break;
      default:
        message.body = getBPRNotificationMessage(alarm);
        message.icon = SvgIcons.bprIcon;}
    
    return html`
      <div
        class="activity-item ${isActiveAlarm
          ? "active"
          : ""}"
         @click=${this.hasAlarmAccess ? (e:Event) => this.handleNotificationClick(e, alarm): null}
       >
        <div class="activity-type">
          <div class="activity-icon">
            ${message.icon}
          </div>
          <div
            class="activity-message ${isActiveAlarm
              ? "active"
              : ""}"
          >
            ${message.body}
          </div>
        </div>
        <div class="activity-actions">
          <div class="activity-time">
            ${formatTimeDifference(alarm.created_at ?? "")}
          </div>
        </div>
      </div>
    `;
  }

  private renderWeatherRoutingAuditLog(auditLog: AuditLog) {

    if (!auditLog) return "";
    
    const isActive = auditLog.status === "unread";
    let hasComment = false;

    const notes = auditLog.notes ? JSON.parse(auditLog.notes) : [];
    if (Array.isArray(notes)) {
      hasComment = notes.some((log) => log.comment?.trim() !== "");
    }
    const isDisabledViewNote = !hasComment;
    const auditlogId = auditLog.id;
    let dismissedCount = 0;
    if(auditLog.mst_status_name === AuditLogStatus.WRADVICEREJECTED){
      dismissedCount = this.getDismissedCountByImo(auditLog.imo);
      this.rejectedLogShown = true;
    }
    const mailAddress = (<any>window)["appenv"].VETMailAddress ?? "";

    const openMailBox = async () => {
      const sID = await FleetNotificationService.fetchSID(auditlogId);
      const mailAddress = (<any>window)["appenv"].VETMailAddress ?? ""; 
      const mailSubject = `${sID} : ${auditLog.imo} : ${auditLog.terminal_from} - ${auditLog.terminal_to}`;

      window.location.href = `mailto:${mailAddress}?subject=${mailSubject}`;
    };
    return html`
      <div
        class="activity-item ${isActive
        ? "active"
        : ""}"
         @click=${(e: Event) => this.weatherRoutingNotificationclick(e, auditLog)}
       >
        <div class="activity-type">
          <div class="activity-icon">
            ${SvgIcons.weaterRoutingIcon}
          </div>
          <div
            class="activity-message ${isActive
        ? "active"
        : ""}"
          >  ${unsafeHTML(getAuditLogMessage(auditLog))}
          </div>
        </div>
        ${this.rejectedLogShown && dismissedCount > 0 ?
        html`<span class="dismissed-count">
            <mc-tag
              fit="small"
              appearance="${dismissedCount > 2 ? "error": "warning"}">
              ${`Dismissed ${this.getDismissedCountByImo(auditLog.imo)} times`}
            </mc-tag>
        </span>` : nothing}
      <div class="activity-actions wr-activity-actions">
        ${this.hasAlarmAccess ? html`<div class="option-icon email"
                        @click=${(e: any) => {
                        e.preventDefault(); 
                        e.stopPropagation();
                        openMailBox(); }
                        }> 
                        ${SvgIcons.customEnvelope}
                        <voyage-tooltip-proxy
                            text=${mailAddress}
                            .positionPriority=${["top"]}
                            .tooltipInteraction=${true}>
                        </voyage-tooltip-proxy>
                    </div>
                    <div class="separator">|</div>`: nothing}
        <div class="add-note" @click=${(e: Event) => this.handleAddNote(e, auditLog, true)}>
          <div>${SvgIcons.activityLogIcon}</div>
          <div>Add note</div>
        </div>
        <div class="separator">|</div>
        <div class="view-note ${isDisabledViewNote ? 'disabled' : ''}"  @click=${(e: Event) => isDisabledViewNote ? nothing : this.handleViewNote(e, auditLog, true)}>
          <div>${SvgIcons.activityLogIcon}</div>
          <div>View note</div>
        </div>
          <div class="separator">|</div>
            <div class="view-note"  @click=${(e: Event) => this.getPdf(auditLog)}>
              <mc-icon slot="trailingicon" icon="square-arrow-up-right" color="black"></mc-icon>
              <div>Advice</div>
            </div>
              </div>

          <div class="activity-time">
            ${formatTimeDifference(auditLog.created_at ?? "")}
           </div>
    `;
  }

  getDismissedCountByImo(imo: number){
    return this.weatherRoutingAuditLogs.filter(log => {
      return (log.imo === imo && log.mst_status_name === AuditLogStatus.WRADVICEREJECTED);
    }).length;
  }
  getPdf(log: AuditLog) {
      const correlationId = log.correlation_id
      if (!correlationId) {
        console.error("Correlation ID not found in metadata");
        return;
      }
          FleetNotificationService.openWRAdvicePDF(correlationId)
  }
  private debounce(func: Function, delay:number) {
    let timeoutId:any;
    return  () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, arguments);
      }, delay);
    };
  }
}
